import { CartModifierGroup, ICartModifierGroup } from './CartModifierGroup';

export interface ICartItem {
    readonly cartItemId: string;
    readonly menuItemId: number;
    readonly menuItemGuid?: string;
    /**
     * Leaf menu group the item was ordered from. An item can belong to multiple
     * groups, so this is captured at add-time from the browse path — it cannot
     * be resolved server-side. Drives group-level prep-station + pricing
     * overrides on the resulting OrderItem (the POS sets the equivalent field).
     */
    readonly menuGroupId?: number;
    /**
     * The ROOT→LEAF menu path this line was PRICED on — the storefront's
     * `pathForItem`, the assistant's chosen candidate. Checkout re-resolves the
     * line on the path that prices it NOW and uses this to say why a price
     * moved ("Happy Hour ended"). A record of what the guest saw, never an
     * input the server prices from (see `repriceCart`). Absent on lines added
     * before it was recorded.
     */
    readonly menuGroupPath?: readonly number[];
    readonly name: string;
    readonly quantity: number;
    readonly price: number; // Unit price (base or selected size price)
    readonly selectedSize?: {
        readonly sizeId?: number;
        readonly sizeName: string; // For display only
    };
    readonly modifierGroups: ICartModifierGroup[];
    readonly notes?: string;
    readonly addedAt: Date;
    readonly taxRateIds: number[];
    readonly taxInclusion: boolean;
    /**
     * COMPOSITE ITEMS. `isComposite` marks the PARENT cart line — the priced
     * container of a "choose N" item; `bundleParentCartItemId` is set on every
     * CHILD and holds the parent's `cartItemId`.
     *
     * Children are real cart lines so they become real ORDER lines, which is
     * what lets each pick keep its own course, prep station, sales category and
     * recipe. Both absent = an ordinary line.
     * See packages/models/src/menus/compositeItemResolver.ts.
     */
    readonly isComposite?: boolean;
    readonly bundleParentCartItemId?: string;
}

export interface ICartItemData {
    cartItemId?: string;
    menuItemId: number;
    menuItemGuid?: string;
    menuGroupId?: number;
    /** See {@link ICartItem.menuGroupPath}. */
    menuGroupPath?: readonly number[];
    name: string;
    quantity?: number;
    price: number; // Unit price (base or selected size price)
    selectedSize?: {
        sizeId?: number;
        sizeName: string; // For display only
    };
    modifierGroups?: CartModifierGroup[];
    notes?: string;
    addedAt?: Date;
    taxRateIds?: number[];
    taxInclusion?: boolean;
    isComposite?: boolean;
    bundleParentCartItemId?: string;
}

/** A stored path, or undefined when it is absent or not a list of whole positive group ids. */
function readMenuGroupPath(value: unknown): number[] | undefined {
    if (!Array.isArray(value) || value.length === 0) return undefined;
    return value.every((id) => typeof id === 'number' && Number.isInteger(id) && id > 0)
        ? [...(value as number[])]
        : undefined;
}

export class CartItem implements ICartItem {
    readonly cartItemId: string;
    readonly menuItemId: number;
    readonly menuItemGuid?: string;
    readonly menuGroupId?: number;
    readonly menuGroupPath?: readonly number[];
    readonly name: string;
    readonly quantity: number;
    readonly price: number; // Unit price (base or selected size price)
    readonly selectedSize?: {
        readonly sizeId?: number;
        readonly sizeName: string; // For display only
    };
    readonly modifierGroups: CartModifierGroup[];
    readonly notes?: string;
    readonly addedAt: Date;
    readonly taxRateIds: number[];
    readonly taxInclusion: boolean;
    readonly isComposite?: boolean;
    readonly bundleParentCartItemId?: string;

    constructor(data: ICartItemData) {
        this.cartItemId = data.cartItemId ?? this.generateCartItemId();
        this.menuItemId = data.menuItemId;
        this.menuItemGuid = data.menuItemGuid;
        this.menuGroupId = data.menuGroupId;
        this.menuGroupPath = data.menuGroupPath ? [...data.menuGroupPath] : undefined;
        this.name = data.name;
        this.quantity = data.quantity ?? 1;
        this.price = data.price;
        this.selectedSize = data.selectedSize;
        this.modifierGroups = data.modifierGroups ?? [];
        this.notes = data.notes;
        this.addedAt = data.addedAt ?? new Date();
        this.taxRateIds = data.taxRateIds ?? [];
        this.taxInclusion = data.taxInclusion ?? false;
        this.isComposite = data.isComposite;
        this.bundleParentCartItemId = data.bundleParentCartItemId;
    }

    private generateCartItemId(): string {
        return `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    get itemPrice(): number {
        return this.price; // Already contains the selected size price if applicable
    }

    get modifiersPrice(): number {
        return this.modifierGroups.reduce((total, group) => total + group.totalPriceDelta, 0);
    }

    get unitPrice(): number {
        return this.itemPrice + this.modifiersPrice;
    }

    get totalPrice(): number {
        return this.unitPrice * this.quantity;
    }

    get isValidConfiguration(): boolean {
        return this.modifierGroups.every(group => group.isValidSelection());
    }

    get hasModifiers(): boolean {
        return this.modifierGroups.some(group => group.modifiers.length > 0);
    }

    get hasNotes(): boolean {
        return !!this.notes && this.notes.trim().length > 0;
    }

    get hasSize(): boolean {
        return !!this.selectedSize;
    }

    toJSON(): ICartItem {
        return {
            cartItemId: this.cartItemId,
            menuItemId: this.menuItemId,
            menuItemGuid: this.menuItemGuid,
            menuGroupId: this.menuGroupId,
            menuGroupPath: this.menuGroupPath,
            name: this.name,
            quantity: this.quantity,
            price: this.price,
            selectedSize: this.selectedSize,
            modifierGroups: this.modifierGroups.map(group => group.toJSON()),
            notes: this.notes,
            addedAt: this.addedAt,
            taxRateIds: this.taxRateIds,
            taxInclusion: this.taxInclusion,
            isComposite: this.isComposite,
            bundleParentCartItemId: this.bundleParentCartItemId
        };
    }

    static fromJSON(data: Record<string, unknown>): CartItem {
        if (typeof data.menuItemId !== 'number') {
            throw new Error("CartItem.fromJSON: menuItemId must be a number.");
        }
        if (typeof data.name !== 'string') {
            throw new Error("CartItem.fromJSON: name must be a string.");
        }
        if (typeof data.price !== 'number') {
            throw new Error("CartItem.fromJSON: price must be a number.");
        }

        const modifierGroups = Array.isArray(data.modifierGroups)
            ? data.modifierGroups.map((group: any) => CartModifierGroup.fromJSON(group))
            : [];

        const addedAt = data.addedAt instanceof Date
            ? data.addedAt
            : typeof data.addedAt === 'string'
                ? new Date(data.addedAt)
                : (data.addedAt && typeof data.addedAt === 'object' && 'toDate' in data.addedAt && typeof (data.addedAt as any).toDate === 'function')
                    ? (data.addedAt as any).toDate()
                    : new Date();

        return new CartItem({
            cartItemId: typeof data.cartItemId === 'string' ? data.cartItemId : undefined,
            // Narrowed like every sibling field below. Leaving this as a raw
            // passthrough is what let a null `menuItemId` reach the cart, then
            // the order, then Firestore — where `OrderItem.fromJSON` throws on
            // read and took the whole orders list down with it.
            //
            // An unusable value becomes NaN rather than 0 ON PURPOSE: 0 is a
            // real sentinel meaning "unmapped line", so producing it here would
            // make a corrupt cart indistinguishable from a legitimately
            // unmapped one. NaN matches nothing, and `OrderItem`'s constructor
            // converts it to the sentinel loudly, at the layer that owns it.
            menuItemId: typeof data.menuItemId === 'number' && !Number.isNaN(data.menuItemId)
                ? data.menuItemId
                : Number(data.menuItemId ?? NaN),
            menuItemGuid: typeof data.menuItemGuid === 'string' ? data.menuItemGuid : undefined,
            menuGroupId: typeof data.menuGroupId === 'number' ? data.menuGroupId : undefined,
            menuGroupPath: readMenuGroupPath(data.menuGroupPath),
            name: data.name,
            quantity: typeof data.quantity === 'number' ? data.quantity : 1,
            price: data.price,
            selectedSize: data.selectedSize as any, // Type assertion for complex nested object
            modifierGroups,
            notes: typeof data.notes === 'string' ? data.notes : undefined,
            addedAt,
            taxRateIds: Array.isArray(data.taxRateIds) ? data.taxRateIds as number[] : [],
            taxInclusion: typeof data.taxInclusion === 'boolean' ? data.taxInclusion : false,
            isComposite: typeof data.isComposite === 'boolean' ? data.isComposite : undefined,
            bundleParentCartItemId:
                typeof data.bundleParentCartItemId === 'string' ? data.bundleParentCartItemId : undefined
        });
    }

    // Helper method to create a copy with updated quantity
    withQuantity(newQuantity: number): CartItem {
        return new CartItem({
            cartItemId: this.cartItemId,
            menuItemId: this.menuItemId,
            menuItemGuid: this.menuItemGuid,
            menuGroupId: this.menuGroupId,
            menuGroupPath: this.menuGroupPath,
            name: this.name,
            quantity: newQuantity,
            price: this.price,
            selectedSize: this.selectedSize,
            modifierGroups: this.modifierGroups, // Use the CartModifierGroup[] array directly
            notes: this.notes,
            addedAt: this.addedAt,
            taxRateIds: this.taxRateIds,
            taxInclusion: this.taxInclusion,
            // Carried deliberately: rebuilding without these turns a composite
            // parent into an ordinary line on any quantity/note edit.
            isComposite: this.isComposite,
            bundleParentCartItemId: this.bundleParentCartItemId
        });
    }

    // Helper method to create a copy with updated notes
    withNotes(newNotes: string): CartItem {
        return new CartItem({
            cartItemId: this.cartItemId,
            menuItemId: this.menuItemId,
            menuItemGuid: this.menuItemGuid,
            menuGroupId: this.menuGroupId,
            menuGroupPath: this.menuGroupPath,
            name: this.name,
            quantity: this.quantity,
            price: this.price,
            selectedSize: this.selectedSize,
            modifierGroups: this.modifierGroups, // Use the CartModifierGroup[] array directly
            notes: newNotes,
            addedAt: this.addedAt,
            taxRateIds: this.taxRateIds,
            taxInclusion: this.taxInclusion,
            // Carried deliberately: rebuilding without these turns a composite
            // parent into an ordinary line on any quantity/note edit.
            isComposite: this.isComposite,
            bundleParentCartItemId: this.bundleParentCartItemId
        });
    }
}
